import { Children, forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Clock, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TRIAGEM_BTN, TRIAGEM_BTN_MANTER } from './triagemTabela';

/**
 * OS BOTÕES DA TRIAGEM, NA MESMA ORDEM EM TODAS (Douglas 30/09/2026).
 *
 * "o manter é quando ele consultou e não teve ainda atualização. se teve
 *  atualização são os outros botões. deixe padrão estes botões em todas as
 *  triagens."
 *
 * A ordem é fixa, da esquerda para a direita:
 *   1. MANTER    — consultei e nada mudou (contorno bordô, sempre o primeiro)
 *   2. AVANÇAR   — o botão que anda o pedido (bordô cheio, um por linha)
 *   3. DESVIOS   — erro, dívida, crédito, reagendar, pendência
 *   4. FERRAMENTAS — não andam a fila: e-mail, print, histórico
 *   5. CANCELAR  — sempre o último
 * Um traço separa os grupos. Todo botão de 1, 2, 3 e 5 conta no placar
 * "Consulta hoje" (`consultaTriagem.tsx`); as ferramentas não.
 *
 * ⛔ Triagem nova monta a coluna Ações com este componente. Não reordene os
 * botões à mão: a pessoa que passa por várias filas no dia aprende UMA posição
 * para o Manter e UMA para o cancelar.
 */
export function AcoesTriagem({ manter, avancar, desvios, ferramentas, cancelar, className }: {
  manter?: ReactNode;
  avancar?: ReactNode;
  desvios?: ReactNode;
  ferramentas?: ReactNode;
  cancelar?: ReactNode;
  className?: string;
}) {
  const grupos = [manter, avancar, desvios, ferramentas, cancelar]
    .filter(g => Children.toArray(g).length > 0);
  return (
    <div className={cn('flex flex-nowrap items-center gap-1.5', className)}>
      {grupos.map((g, i) => (
        <span key={i} className="contents">
          {i > 0 && <span aria-hidden className="mx-0.5 h-4 w-px flex-shrink-0 bg-border" />}
          {g}
        </span>
      ))}
    </div>
  );
}

type BotaoProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & { carregando?: boolean };

/** O Manter padrão: relógio em contorno bordô. O `title` diz o que ele registra. */
export const BotaoManter = forwardRef<HTMLButtonElement, BotaoProps>(function BotaoManter(
  { carregando, className, title, ...props }, ref) {
  return (
    <button ref={ref} type="button" className={cn(TRIAGEM_BTN_MANTER, className)}
      title={title ?? 'Manter — consultei e ainda não mudou nada. Vai para o fim da fila.'} {...props}>
      {carregando ? <Loader2 className="animate-spin" /> : <Clock />}
    </button>
  );
});

/** O cancelar padrão: X, sempre o último da linha. */
export const BotaoCancelar = forwardRef<HTMLButtonElement, BotaoProps>(function BotaoCancelar(
  { carregando, className, title, ...props }, ref) {
  return (
    <button ref={ref} type="button" className={cn(TRIAGEM_BTN, className)}
      title={title ?? 'Cancelar pedido'} {...props}>
      {carregando ? <Loader2 className="animate-spin" /> : <X />}
    </button>
  );
});
